// ============================================================
// LinuxDiskManagement.jsx
//
// Branded, responsive course content page for the
// "Linux Disk Management" lesson.
//
// Brand palette (consistent across all lesson pages):
//   Background  → #100720  (deep purple-black)
//   Primary     → goldenrod / #DAA520
//   Accent      → #FFD700  (bright gold highlights)
//   Text        → #FFFFFF  (white)
//   Surface     → rgba(255,255,255,0.04) with gold borders
//
// Built with:
//   - Material UI (MUI v5) for layout, typography & icons
//   - Fully responsive (xs → xl breakpoints)
//   - Heavily commented for junior developers
// ============================================================

import React, { useState } from "react";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Collapse,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import StorageIcon             from "@mui/icons-material/Storage";
import MemoryIcon              from "@mui/icons-material/Memory";
import SearchIcon              from "@mui/icons-material/Search";
import LayersIcon              from "@mui/icons-material/Layers";
import BuildIcon               from "@mui/icons-material/Build";
import FormatColorFillIcon     from "@mui/icons-material/FormatColorFill";
import FolderOpenIcon          from "@mui/icons-material/FolderOpen";
import SettingsIcon            from "@mui/icons-material/Settings";
import SyncIcon                from "@mui/icons-material/Sync";
import TerminalIcon            from "@mui/icons-material/Terminal";
import LinkIcon                from "@mui/icons-material/Link";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import ExpandLessIcon          from "@mui/icons-material/ExpandLess";
import ContentCopyIcon         from "@mui/icons-material/ContentCopy";
import DoneIcon                from "@mui/icons-material/Done";
import CheckCircleOutlineIcon  from "@mui/icons-material/CheckCircleOutline";
import LightbulbIcon           from "@mui/icons-material/Lightbulb";
import StarIcon                from "@mui/icons-material/Star";
import SchoolIcon              from "@mui/icons-material/School";
import WarningAmberIcon        from "@mui/icons-material/WarningAmber";
import DangerousIcon           from "@mui/icons-material/Dangerous";
import CheckCircleIcon         from "@mui/icons-material/CheckCircle";
import DnsIcon                 from "@mui/icons-material/Dns";
import SwapVertIcon            from "@mui/icons-material/SwapVert";
import SpeedIcon               from "@mui/icons-material/Speed";
import AccountTreeIcon         from "@mui/icons-material/AccountTree";
import ArticleIcon             from "@mui/icons-material/Article";

// ── Brand tokens ──────────────────────────────────────────
// Centralise all colours here — change once to retheme everything.
const BRAND = {
    bg:            "#233D4D",
    surface:       "rgba(255,255,255,0.04)",
    border:        "rgba(218,165,32,0.25)",
    borderHover:   "rgba(218,165,32,0.6)",
    gold:          "#DAA520",
    goldBright:    "#FFD700",
    goldGlow:      "rgba(218,165,32,0.15)",
    white:         "#FFFFFF",
    muted:         "rgba(255,255,255,0.55)",
    codeBg:        "rgba(0,0,0,0.45)",
    codeText:      "#FFD700",
    danger:        "rgba(255,80,80,0.12)",
    dangerBorder:  "rgba(255,80,80,0.35)",
    dangerText:    "#ff8080",
    success:       "rgba(39,201,63,0.10)",
    successBorder: "rgba(39,201,63,0.35)",
    successText:   "#27c93f",
    // Filesystem type colours used in the comparison table
    fsExt4:  "#64c8ff",   // blue
    fsXfs:   "#a78bfa",   // violet
    fsBtrfs: "#34d399",   // green
    fsFat:   "#fbbf24",   // amber
    fsNtfs:  "#f97316",   // orange
    fsSwap:  "#f472b6",   // pink
};

// ── Shared heading style ──────────────────────────────────
const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Shared table cell style helper ───────────────────────
// Keeps MUI TableCell styling DRY — used in several tables.
const tcStyle = (extra = {}) => ({
    borderColor: BRAND.border,
    color: BRAND.muted,
    fontSize: 13,
    py: 1.4,
    ...extra,
});

// ============================================================
//  SUB-COMPONENT: SectionTitle
//  Gold-icon circle + heading. Reused by every section.
//  Props:
//    icon  – MUI icon element
//    label – heading text string
// ============================================================
function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "50%",
                    background: BRAND.goldGlow,
                    border: `1px solid ${BRAND.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                }}
            >
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ConceptCard
//  Hoverable card for one disk-management concept.
//  Props:
//    icon        – MUI icon element
//    title       – short concept name
//    description – plain-English explanation
// ============================================================
function ConceptCard({ icon, title, description }) {
    return (
        <Paper
            elevation={0}
            sx={{
                p: 2.5,
                height: "100%",
                background: BRAND.surface,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 3,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 20px ${BRAND.goldGlow}`,
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.2 }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 22 } })}
                <Typography
                    sx={{ fontFamily: "'Georgia', serif", fontWeight: 700, color: BRAND.white, fontSize: 14 }}
                >
                    {title}
                </Typography>
            </Box>
            <Typography sx={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.65 }}>
                {description}
            </Typography>
        </Paper>
    );
}

// ============================================================
//  SUB-COMPONENT: CodeBlock
//  Terminal-style block with copy-to-clipboard button.
//  Props:
//    lines – array of { cmd: string, comment?: string }
// ============================================================
function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);

    // Join all commands for clipboard
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box
            sx={{
                background: BRAND.codeBg,
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
            }}
        >
            {/* ── Terminal title bar ── */}
            <Box
                sx={{
                    px: 2,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom: `1px solid ${BRAND.border}`,
                    background: "rgba(0,0,0,0.3)",
                }}
            >
                {/* Decorative macOS-style dots */}
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>

                {/* Copy button with tick feedback */}
                <Tooltip title={copied ? "Copied!" : "Copy commands"} placement="top">
                    <Box
                        onClick={handleCopy}
                        sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: copied ? BRAND.goldBright : BRAND.muted,
                            fontSize: 12,
                            transition: "color 0.2s",
                            "&:hover": { color: BRAND.gold },
                        }}
                    >
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>

            {/* ── Command lines ── */}
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {/* Optional grey comment above each command */}
                        {comment && (
                            <Typography
                                sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.3 }}
                            >
                                # {comment}
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                fontFamily: "monospace",
                                fontSize: { xs: 12, md: 13 },
                                color: BRAND.codeText,
                                wordBreak: "break-all",
                            }}
                        >
                            {cmd}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: WarningBox
//  Red-tinted warning panel for destructive-action notices.
//  Props:
//    children – JSX content
// ============================================================
function WarningBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                mb: 2,
                background: BRAND.danger,
                border: `1px solid ${BRAND.dangerBorder}`,
                borderRadius: 2,
            }}
        >
            <WarningAmberIcon sx={{ color: BRAND.dangerText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.dangerText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: SuccessBox
//  Green-tinted best-practice tip box.
//  Props:
//    children – JSX content
// ============================================================
function SuccessBox({ children }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                p: 2,
                mb: 2,
                background: BRAND.success,
                border: `1px solid ${BRAND.successBorder}`,
                borderRadius: 2,
            }}
        >
            <CheckCircleIcon sx={{ color: BRAND.successText, flexShrink: 0, mt: 0.1 }} />
            <Typography sx={{ color: BRAND.successText, fontSize: 13, lineHeight: 1.7 }}>
                {children}
            </Typography>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ExpandableExample
//  Accordion for optional deeper content sections.
//  Props:
//    title    – accordion header label
//    icon     – MUI icon element
//    children – JSX body content
// ============================================================
function ExpandableExample({ title, icon, children }) {
    const [open, setOpen] = useState(false);

    return (
        <Box
            sx={{
                border: `1px solid ${open ? BRAND.borderHover : BRAND.border}`,
                borderRadius: 2.5,
                overflow: "hidden",
                mb: 1.5,
                transition: "border-color 0.25s",
            }}
        >
            {/* Clickable header */}
            <Box
                onClick={() => setOpen((p) => !p)}
                sx={{
                    px: 2.5,
                    py: 1.8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    background: open ? BRAND.goldGlow : BRAND.surface,
                    transition: "background 0.25s",
                    "&:hover": { background: BRAND.goldGlow },
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                    {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                    <Typography sx={{ color: BRAND.white, fontWeight: 600, fontSize: 14 }}>
                        {title}
                    </Typography>
                </Box>
                {open
                    ? <ExpandLessIcon sx={{ color: BRAND.gold }} />
                    : <ExpandMoreIcon sx={{ color: BRAND.muted }} />
                }
            </Box>

            {/* Smooth collapse animation via MUI Collapse */}
            <Collapse in={open}>
                <Box
                    sx={{
                        px: 2.5,
                        py: 2,
                        background: "rgba(0,0,0,0.25)",
                        borderTop: `1px solid ${BRAND.border}`,
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
}

// ============================================================
//  SUB-COMPONENT: ResourceLink
//  Styled external-link row.
//  Props:
//    href  – URL
//    label – display name
//    sub   – short description
// ============================================================
function ResourceLink({ href, label, sub }) {
    return (
        <Box
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mb: 1.2,
                textDecoration: "none",
                border: `1px solid ${BRAND.border}`,
                borderRadius: 2,
                background: BRAND.surface,
                transition: "border-color 0.25s, box-shadow 0.25s",
                "&:hover": {
                    borderColor: BRAND.borderHover,
                    boxShadow: `0 0 16px ${BRAND.goldGlow}`,
                },
            }}
        >
            <LinkIcon sx={{ color: BRAND.gold, fontSize: 20, flexShrink: 0 }} />
            <Box>
                <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, lineHeight: 1.3 }}>
                    {label}
                </Typography>
                {sub && (
                    <Typography sx={{ color: BRAND.muted, fontSize: 12, mt: 0.3 }}>{sub}</Typography>
                )}
            </Box>
        </Box>
    );
}

// ============================================================
//  MAIN COMPONENT: LinuxDiskManagement
//  Full "Linux Disk Management" lesson page.
// ============================================================
export default function LinuxDiskManagement() {
    return (
        // ── Page wrapper ─────────────────────────────────────
        <Box
            sx={{
                minHeight: "100vh",
                background: BRAND.bg,
                // Subtle diagonal texture — consistent with all lesson pages
                backgroundImage: `repeating-linear-gradient(
          -45deg,
          rgba(218,165,32,0.02) 0px,
          rgba(218,165,32,0.02) 1px,
          transparent 1px,
          transparent 12px
        )`,
                py: { xs: 4, md: 7 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="lg">

                {/* ══════════════════════════════════════════════
            HERO HEADER
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>

                    {/* Module badge */}
                    <Chip
                        icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />}
                        label="Systems · Module 05"
                        sx={{
                            mb: 2.5,
                            background: BRAND.goldGlow,
                            border: `1px solid ${BRAND.border}`,
                            color: BRAND.gold,
                            fontFamily: "monospace",
                            fontSize: 11,
                            letterSpacing: 1,
                            textTransform: "uppercase",
                        }}
                    />

                    {/* Title with gold gradient on key word */}
                    <Typography
                        variant="h3"
                        sx={{
                            ...sectionHeading,
                            fontSize: { xs: 26, sm: 34, md: 42 },
                            mb: 2,
                            "& span": {
                                background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldBright})`,
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            },
                        }}
                    >
                        Linux <span>Disk Management</span>
                    </Typography>

                    {/* Intro */}
                    <Typography
                        sx={{
                            color: BRAND.muted,
                            fontSize: { xs: 14, md: 16 },
                            maxWidth: 680,
                            lineHeight: 1.8,
                            mx: { xs: "auto", md: 0 },
                            mb: 3,
                        }}
                    >
                        Storage management is a core sysadmin skill. Linux gives you precise, low-level
                        control over every disk: how it is partitioned, which filesystem it uses, where it
                        is mounted, and whether that mount survives a reboot. This lesson covers the full
                        workflow — from a raw new disk to a persistent, production-ready mount.
                    </Typography>

                    {/* Quick stats */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        {[
                            { label: "~3 hours",     sub: "read time"     },
                            { label: "Intermediate", sub: "level"          },
                            { label: "5 stages",     sub: "full workflow"  },
                        ].map(({ label, sub }) => (
                            <Box
                                key={label}
                                sx={{
                                    textAlign: "center",
                                    background: BRAND.surface,
                                    border: `1px solid ${BRAND.border}`,
                                    borderRadius: 2,
                                    px: 2.5,
                                    py: 1.2,
                                }}
                            >
                                <Typography sx={{ color: BRAND.goldBright, fontWeight: 800, fontSize: 15 }}>{label}</Typography>
                                <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{sub}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ══════════════════════════════════════════════
            SECTION 1 – KEY CONCEPTS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LayersIcon />} label="Key Concepts" />

                    <Grid container spacing={2}>
                        {[
                            {
                                icon: <StorageIcon />,
                                title: "Block Devices",
                                description:
                                    "Disks in Linux are represented as block device files in /dev. /dev/sda is the first SATA/SCSI disk, /dev/nvme0n1 is the first NVMe drive, /dev/vda is a virtual disk. Partitions are suffixed: /dev/sda1, /dev/sda2.",
                            },
                            {
                                icon: <LayersIcon />,
                                title: "Partition Tables",
                                description:
                                    "A partition table is written at the start of a disk describing how it is divided. MBR (Master Boot Record) supports up to 4 primary partitions and 2 TB disks. GPT (GUID Partition Table) supports 128 partitions and disks over 2 TB — always use GPT for modern systems.",
                            },
                            {
                                icon: <FormatColorFillIcon />,
                                title: "Filesystems",
                                description:
                                    "A filesystem defines how data is stored and organised inside a partition. Common Linux filesystems: ext4 (default, reliable), xfs (high performance), btrfs (modern, snapshots), and vfat (cross-platform USB drives).",
                            },
                            {
                                icon: <FolderOpenIcon />,
                                title: "Mount Points",
                                description:
                                    "To access a formatted partition, you must mount it to a directory. The directory becomes the entry point for all files on that partition. You can mount at /mnt/data, /home, /var — anywhere in the tree.",
                            },
                            {
                                icon: <SettingsIcon />,
                                title: "/etc/fstab",
                                description:
                                    "The fstab file tells Linux which filesystems to mount automatically at boot. Each entry defines the device (by UUID), mount point, filesystem type, options, and two numeric fields for fsck and dump.",
                            },
                            {
                                icon: <SwapVertIcon />,
                                title: "Swap Space",
                                description:
                                    "Swap is disk space used as overflow when RAM is full. It can be a dedicated partition or a swap file. Swap is slower than RAM but prevents out-of-memory crashes. Modern systems with lots of RAM often use swapfiles for flexibility.",
                            },
                        ].map((card) => (
                            <Grid item xs={12} sm={6} md={4} key={card.title}>
                                <ConceptCard {...card} />
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 2 – VIEWING DISKS & PARTITIONS
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SearchIcon />} label="View Disks & Partitions" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Before touching any disk, always inspect what the system sees. These read-only
                        commands are completely safe — they do not modify anything.
                    </Typography>

                    {/* ── Basic listing ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Basic Disk Listing
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ lsblk",                    comment: "Tree view of all block devices and their mount points" },
                            { cmd: "$ lsblk -f",                 comment: "Same but also shows filesystem type and UUID" },
                            { cmd: "$ lsblk -o NAME,SIZE,TYPE,MOUNTPOINT,FSTYPE,UUID", comment: "Custom columns — the most useful lsblk one-liner" },
                            { cmd: "$ sudo fdisk -l",             comment: "Full partition table for every disk (requires root)" },
                            { cmd: "$ sudo fdisk -l /dev/sda",   comment: "Partition table for one specific disk only" },
                            { cmd: "$ sudo parted -l",            comment: "GPT-aware partition listing (better for modern disks)" },
                        ]}
                    />

                    {/* ── Mounted filesystems ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Viewing Mounted Filesystems
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ df -h",                    comment: "Show disk space used/available for all mounts (human-readable)" },
                            { cmd: "$ df -hT",                   comment: "Same but includes filesystem type column" },
                            { cmd: "$ mount | column -t",         comment: "List all active mounts, neatly aligned in columns" },
                            { cmd: "$ findmnt",                  comment: "Beautiful tree view of the entire mount namespace" },
                            { cmd: "$ findmnt /dev/sda1",         comment: "Show mount info for one specific device" },
                            { cmd: "$ cat /proc/mounts",          comment: "Raw kernel mount table — same data as mount command" },
                        ]}
                    />

                    {/* ── Identifying disks ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Identifying Disks & UUIDs
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo blkid",               comment: "Print UUID, label, and filesystem type for all devices" },
                            { cmd: "$ sudo blkid /dev/sdb1",     comment: "Details for one specific partition" },
                            { cmd: "$ ls -la /dev/disk/by-uuid/",comment: "Symlinks from UUID → device path (what fstab uses)" },
                            { cmd: "$ ls -la /dev/disk/by-label/",comment: "Symlinks from partition label → device path" },
                            { cmd: "$ sudo hdparm -I /dev/sda",  comment: "Detailed info about a SATA disk (model, serial, speed)" },
                            { cmd: "$ sudo smartctl -a /dev/sda",comment: "SMART health data (install: sudo apt install smartmontools)" },
                        ]}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 3 – PARTITIONING
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<BuildIcon />} label="Partitioning a Disk" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 2 }}>
                        Partitioning divides a raw disk into sections. You must partition before formatting.
                        Linux has two main partitioning tools:{" "}
                        <code style={{ color: BRAND.goldBright }}>fdisk</code> (MBR and GPT, interactive menu)
                        and <code style={{ color: BRAND.goldBright }}>parted</code> (GPT-native, scriptable).
                        For modern systems with disks over 2 TB, always use GPT.
                    </Typography>

                    {/* Destructive action warning */}
                    <WarningBox>
                        <strong>Destructive operation:</strong> Partitioning wipes data. Always confirm the
                        correct device with <code>lsblk</code> first. A typo like{" "}
                        <code>/dev/sda</code> instead of <code>/dev/sdb</code> can destroy your OS disk.
                        Work on virtual machines until you are confident.
                    </WarningBox>

                    {/* ── fdisk workflow ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 fdisk — Interactive Menu (MBR & GPT)
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo fdisk /dev/sdb",      comment: "Open fdisk on the second disk — launches an interactive menu" },
                        ]}
                    />

                    {/* fdisk key commands inside the interactive menu */}
                    <Box
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflow: "hidden",
                            mb: 3,
                        }}
                    >
                        {/* Table header */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1,
                                background: BRAND.goldGlow,
                                borderBottom: `1px solid ${BRAND.border}`,
                                display: { xs: "none", sm: "flex" },
                                gap: 2,
                            }}
                        >
                            {["Key", "Action"].map((h) => (
                                <Typography key={h} sx={{ fontSize: 11, color: BRAND.gold, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase", minWidth: h === "Key" ? 60 : "auto" }}>
                                    {h}
                                </Typography>
                            ))}
                        </Box>

                        {/* fdisk menu keys */}
                        {[
                            { key: "m",  action: "Print help menu — lists all available commands" },
                            { key: "p",  action: "Print current partition table" },
                            { key: "g",  action: "Create a new empty GPT partition table" },
                            { key: "o",  action: "Create a new empty MBR partition table" },
                            { key: "n",  action: "Add a new partition (follow the interactive prompts)" },
                            { key: "d",  action: "Delete a partition" },
                            { key: "t",  action: "Change a partition type (e.g. 82 = Linux swap, 83 = Linux)" },
                            { key: "w",  action: "Write changes to disk and exit (commits all changes)" },
                            { key: "q",  action: "Quit without saving — safe if you made a mistake" },
                        ].map(({ key, action }) => (
                            <Box
                                key={key}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    px: 2,
                                    py: 1.2,
                                    borderBottom: `1px solid ${BRAND.border}`,
                                    "&:last-child": { borderBottom: "none" },
                                    "&:hover": { background: "rgba(255,255,255,0.02)" },
                                    transition: "background 0.2s",
                                }}
                            >
                                {/* Single-letter key in gold monospace */}
                                <Typography
                                    sx={{
                                        fontFamily: "monospace",
                                        fontSize: 15,
                                        fontWeight: 700,
                                        color: BRAND.goldBright,
                                        minWidth: 40,
                                        textAlign: "center",
                                        background: "rgba(218,165,32,0.1)",
                                        border: `1px solid ${BRAND.border}`,
                                        borderRadius: 1,
                                        px: 1,
                                        py: 0.2,
                                        flexShrink: 0,
                                    }}
                                >
                                    {key}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: BRAND.muted }}>{action}</Typography>
                            </Box>
                        ))}
                    </Box>

                    {/* ── parted commands ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 parted — GPT-Native & Scriptable
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo parted /dev/sdb",                                    comment: "Open interactive parted session" },
                            { cmd: "$ sudo parted /dev/sdb print",                              comment: "Print partition table without entering interactive mode" },
                            { cmd: "$ sudo parted /dev/sdb mklabel gpt",                        comment: "Create a new GPT partition table (wipes disk!)" },
                            { cmd: "$ sudo parted /dev/sdb mkpart primary ext4 1MiB 100%",      comment: "Create one partition using the full disk" },
                            { cmd: "$ sudo parted /dev/sdb mkpart primary ext4 1MiB 50GiB",     comment: "Create a 50 GiB partition starting at 1 MiB" },
                            { cmd: "$ sudo parted /dev/sdb set 1 boot on",                      comment: "Mark partition 1 as bootable" },
                        ]}
                    />

                    {/* Deeper: partitioning examples */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 2, mt: 3 }}>
                        🔽 Advanced Partitioning (click to expand)
                    </Typography>

                    <ExpandableExample title="Scripted partition creation with parted (non-interactive)" icon={<BuildIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            You can chain all <code style={{ color: BRAND.goldBright }}>parted</code> commands
                            in a single shell line — useful in provisioning scripts and automation pipelines.
                            This avoids the interactive prompt entirely.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo parted -s /dev/sdb mklabel gpt",                  comment: "-s = script mode (no interactive prompts)" },
                                { cmd: "$ sudo parted -s /dev/sdb mkpart primary ext4 1MiB 20GiB", comment: "First partition: 20 GiB for the OS" },
                                { cmd: "$ sudo parted -s /dev/sdb mkpart primary linux-swap 20GiB 24GiB", comment: "Second partition: 4 GiB swap" },
                                { cmd: "$ sudo parted -s /dev/sdb mkpart primary ext4 24GiB 100%", comment: "Third partition: rest of disk for data" },
                                { cmd: "$ sudo partprobe /dev/sdb",                              comment: "Notify the kernel of partition table changes (no reboot needed)" },
                            ]}
                        />
                    </ExpandableExample>

                    <ExpandableExample title="Using gdisk for GPT-only partitioning" icon={<DnsIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            <code style={{ color: BRAND.goldBright }}>gdisk</code> is a GPT-exclusive tool —
                            think of it as fdisk but for GPT only. It uses the same key-based menu as fdisk,
                            making it familiar to anyone who already knows fdisk. Install with{" "}
                            <code style={{ color: BRAND.goldBright }}>sudo apt install gdisk</code>.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo gdisk /dev/sdb",     comment: "Open gdisk interactive menu on /dev/sdb" },
                                { cmd: "$ sudo gdisk -l /dev/sdb",  comment: "Print GPT partition table without entering interactive mode" },
                                { cmd: "$ sudo sgdisk -p /dev/sdb", comment: "sgdisk: scriptable gdisk — print partition table" },
                                { cmd: "$ sudo sgdisk -n 0:0:+20G -t 0:8300 /dev/sdb", comment: "Create a 20 GiB Linux filesystem partition" },
                                { cmd: "$ sudo sgdisk -n 0:0:+4G  -t 0:8200 /dev/sdb", comment: "Create a 4 GiB Linux swap partition" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 4 – FILESYSTEM COMPARISON TABLE
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AccountTreeIcon />} label="Filesystem Comparison" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        Choosing the right filesystem depends on your use case. Here is a quick reference
                        to the most common Linux filesystems.
                    </Typography>

                    {/*
            MUI TableContainer gives us horizontal scroll on small screens
            so the table never breaks the layout on mobile.
          */}
                    <TableContainer
                        component={Box}
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflowX: "auto",
                        }}
                    >
                        <Table size="small" sx={{ minWidth: 540 }}>
                            <TableHead>
                                <TableRow sx={{ background: BRAND.goldGlow }}>
                                    {["Filesystem", "Type", "Max File Size", "Best For", "mkfs command"].map((h) => (
                                        <TableCell
                                            key={h}
                                            sx={{
                                                ...tcStyle({ color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", borderColor: BRAND.border }),
                                            }}
                                        >
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    {
                                        fs: "ext4",    color: BRAND.fsExt4,  type: "Journalled",    maxFile: "16 TiB",  best: "General Linux use — default everywhere",       cmd: "mkfs.ext4 /dev/sdb1",  },
                                    {
                                        fs: "xfs",     color: BRAND.fsXfs,   type: "Journalled",    maxFile: "8 EiB",   best: "Large files, databases, high throughput",       cmd: "mkfs.xfs /dev/sdb1",   },
                                    {
                                        fs: "btrfs",   color: BRAND.fsBtrfs, type: "Copy-on-Write", maxFile: "16 EiB",  best: "Snapshots, RAID, modern workloads",             cmd: "mkfs.btrfs /dev/sdb1", },
                                    {
                                        fs: "vfat",    color: BRAND.fsFat,   type: "FAT32",         maxFile: "4 GiB",   best: "USB drives, cross-platform compatibility",      cmd: "mkfs.vfat /dev/sdb1",  },
                                    {
                                        fs: "exfat",   color: BRAND.fsFat,   type: "exFAT",         maxFile: "16 EiB",  best: "Large USB drives, macOS/Windows/Linux compat",  cmd: "mkfs.exfat /dev/sdb1", },
                                    {
                                        fs: "ntfs",    color: BRAND.fsNtfs,  type: "Journalled",    maxFile: "16 EiB",  best: "Reading Windows disks (write support via ntfs-3g)", cmd: "mkfs.ntfs /dev/sdb1",},
                                    {
                                        fs: "swap",    color: BRAND.fsSwap,  type: "Swap",          maxFile: "—",       best: "RAM overflow area, hibernation",                cmd: "mkswap /dev/sdb1",     },
                                ].map(({ fs, color, type, maxFile, best, cmd }) => (
                                    <TableRow
                                        key={fs}
                                        sx={{
                                            "&:hover": { background: "rgba(255,255,255,0.02)" },
                                            transition: "background 0.2s",
                                        }}
                                    >
                                        {/* Filesystem name with colour chip */}
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>
                                            <Chip
                                                label={fs}
                                                size="small"
                                                sx={{
                                                    background: `${color}18`,
                                                    border: `1px solid ${color}55`,
                                                    color: color,
                                                    fontFamily: "monospace",
                                                    fontSize: 11,
                                                    height: 22,
                                                    fontWeight: 700,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>{type}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border, fontFamily: "monospace", color: BRAND.codeText })}>{maxFile}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>{best}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border, fontFamily: "monospace", color: BRAND.codeText, fontSize: 12 })}>
                                            {cmd}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 5 – FORMATTING
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FormatColorFillIcon />} label="Formatting a Partition" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 2 }}>
                        After creating a partition, you must write a filesystem onto it with a{" "}
                        <code style={{ color: BRAND.goldBright }}>mkfs.*</code> command. The filesystem you
                        choose determines features like journalling, maximum file size, and performance
                        characteristics. For most Linux data disks, <strong style={{ color: BRAND.white }}>ext4</strong> is
                        the safe default. Use <strong style={{ color: BRAND.white }}>xfs</strong> for large
                        files or databases.
                    </Typography>

                    {/* Warning: formatting destroys data */}
                    <WarningBox>
                        <strong>Destructive operation:</strong> Formatting permanently erases all data on
                        the partition. Triple-check you have the right device (e.g.{" "}
                        <code>/dev/sdb1</code>, not <code>/dev/sda1</code>) before running any mkfs command.
                    </WarningBox>

                    {/* ── Common mkfs commands ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Common Format Commands
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo mkfs.ext4 /dev/sdb1",                       comment: "Format partition as ext4 — the Linux standard" },
                            { cmd: "$ sudo mkfs.ext4 -L 'MyData' /dev/sdb1",           comment: "Format ext4 and assign a human-readable label" },
                            { cmd: "$ sudo mkfs.xfs /dev/sdb1",                        comment: "Format as XFS — great for large files and databases" },
                            { cmd: "$ sudo mkfs.btrfs /dev/sdb1",                      comment: "Format as Btrfs — modern filesystem with snapshot support" },
                            { cmd: "$ sudo mkfs.vfat -F 32 /dev/sdb1",                 comment: "Format as FAT32 — for USB drives shared with Windows/macOS" },
                            { cmd: "$ sudo mkfs.exfat /dev/sdb1",                      comment: "Format as exFAT — for large USB drives (>4 GiB files)" },
                            { cmd: "$ sudo mkswap /dev/sdb2",                          comment: "Set up a swap partition (use swapon to activate it)" },
                            { cmd: "$ sudo tune2fs -L 'DataDisk' /dev/sdb1",           comment: "Change the label of an already-formatted ext4 partition" },
                            { cmd: "$ sudo tune2fs -l /dev/sdb1",                      comment: "Read ext4 partition metadata (label, UUID, block count)" },
                        ]}
                    />

                    <ExpandableExample title="Formatting multiple partitions in one script" icon={<FormatColorFillIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            When provisioning a new server you often need to format several partitions at once.
                            Here is a practical script pattern for a three-partition layout: OS data, swap, and
                            a large data volume.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo mkfs.ext4 -L 'root-data' /dev/sdb1",     comment: "Format first partition as ext4 with a label" },
                                { cmd: "$ sudo mkswap -L 'swap'         /dev/sdb2",     comment: "Format second partition as swap with a label" },
                                { cmd: "$ sudo mkfs.xfs  -L 'data'      /dev/sdb3",     comment: "Format third partition as XFS for data storage" },
                                { cmd: "$ sudo swapon /dev/sdb2",                        comment: "Activate the swap partition immediately" },
                                { cmd: "$ swapon --show",                                comment: "Confirm swap is active" },
                            ]}
                        />
                    </ExpandableExample>

                    <ExpandableExample title="Wiping a disk completely before reuse" icon={<DangerousIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Sometimes you need to completely wipe a disk before reformatting — to remove old
                            partition tables, headers, or sensitive data. Use these tools carefully.
                        </Typography>
                        <WarningBox>
                            These commands are <strong>irreversible</strong>. All data will be gone. Use only
                            on disks you intend to completely reuse.
                        </WarningBox>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo wipefs -a /dev/sdb",                     comment: "Remove all filesystem and partition table signatures" },
                                { cmd: "$ sudo dd if=/dev/zero of=/dev/sdb bs=1M count=10", comment: "Zero out the first 10 MiB (wipes partition table)" },
                                { cmd: "$ sudo dd if=/dev/urandom of=/dev/sdb bs=1M",   comment: "Overwrite entire disk with random data (secure wipe — slow)" },
                                { cmd: "$ sudo shred -v -n 3 /dev/sdb",                 comment: "DoD-style 3-pass overwrite for secure data destruction" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 6 – MOUNTING & UNMOUNTING
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<FolderOpenIcon />} label="Mounting & Unmounting" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        A formatted partition is not accessible until it is{" "}
                        <strong style={{ color: BRAND.white }}>mounted</strong> to a directory. The directory
                        (mount point) must exist before you can mount to it. Any files previously inside that
                        directory will be hidden while something is mounted over it.
                    </Typography>

                    {/* ── Basic mount/unmount ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Basic Mount & Unmount
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo mkdir -p /mnt/data",              comment: "Create the mount point directory (-p avoids error if it exists)" },
                            { cmd: "$ sudo mount /dev/sdb1 /mnt/data",       comment: "Mount the partition at /mnt/data" },
                            { cmd: "$ ls /mnt/data",                         comment: "Verify the partition contents are accessible" },
                            { cmd: "$ df -h /mnt/data",                      comment: "Check how much space this partition has" },
                            { cmd: "$ sudo umount /mnt/data",                comment: "Unmount by mount point (safe — flushes buffers first)" },
                            { cmd: "$ sudo umount /dev/sdb1",                comment: "Unmount by device name (same result)" },
                            { cmd: "$ sudo umount -l /mnt/data",             comment: "Lazy unmount — detaches immediately, cleans up when idle" },
                        ]}
                    />

                    {/* ── Mount options ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2, mt: 3 }}>
                        🟡 Common Mount Options
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo mount -o ro /dev/sdb1 /mnt/data",           comment: "Mount read-only (ro) — safe for backups or untrusted disks" },
                            { cmd: "$ sudo mount -o rw,noatime /dev/sdb1 /mnt/data",   comment: "Read-write, noatime = don't update access time on read (faster)" },
                            { cmd: "$ sudo mount -o remount,ro /mnt/data",             comment: "Remount an already-mounted filesystem as read-only" },
                            { cmd: "$ sudo mount -t ext4 /dev/sdb1 /mnt/data",         comment: "Explicitly specify filesystem type (usually auto-detected)" },
                            { cmd: "$ sudo mount -o loop disk.img /mnt/img",           comment: "Mount a disk image file using a loop device" },
                        ]}
                    />

                    <ExpandableExample title="Troubleshooting 'device is busy' on umount" icon={<TerminalIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            If <code style={{ color: BRAND.goldBright }}>umount</code> gives you a{" "}
                            <em>"target is busy"</em> error, something is using the filesystem. Use{" "}
                            <code style={{ color: BRAND.goldBright }}>fuser</code> or{" "}
                            <code style={{ color: BRAND.goldBright }}>lsof</code> to find and handle it.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo fuser -m /mnt/data",              comment: "List all PIDs using /mnt/data" },
                                { cmd: "$ sudo fuser -km /mnt/data",             comment: "Kill all processes using it (use with care!)" },
                                { cmd: "$ sudo lsof /mnt/data",                  comment: "Detailed list of open files on this mount" },
                                { cmd: "$ sudo lsof +D /mnt/data",               comment: "Show all files opened recursively under /mnt/data" },
                                { cmd: "$ sudo umount -l /mnt/data",             comment: "If you can't kill processes, use lazy unmount as last resort" },
                            ]}
                        />
                    </ExpandableExample>

                    <ExpandableExample title="Binding and stacking mounts" icon={<LayersIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            A <strong style={{ color: BRAND.white }}>bind mount</strong> makes a directory
                            accessible at a second path — without copying data. Useful in containers and
                            chroot environments. Changes in either location affect the same data.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo mount --bind /var/www /mnt/www-backup",   comment: "Bind /var/www to /mnt/www-backup (same data, two paths)" },
                                { cmd: "$ sudo mount --bind /home/alice /mnt/alice",     comment: "Expose one user's home at another path" },
                                { cmd: "$ sudo mount --rbind /dev /chroot/dev",          comment: "--rbind also binds all submounts recursively (needed in chroots)" },
                                { cmd: "$ sudo umount /mnt/www-backup",                  comment: "Unmount the bind — original /var/www is untouched" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 7 – PERSISTENT MOUNTS (FSTAB)
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SyncIcon />} label="Persistent Mounts — /etc/fstab" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 2 }}>
                        A manual mount disappears on reboot. To make a mount permanent, add an entry to{" "}
                        <code style={{ color: BRAND.goldBright }}>/etc/fstab</code>. Always use UUID instead
                        of device paths like <code style={{ color: BRAND.goldBright }}>/dev/sdb1</code> —
                        UUIDs never change if you add or rearrange disks.
                    </Typography>

                    {/* fstab field explainer */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.5 }}>
                        🟡 The 6 fstab Fields
                    </Typography>

                    <TableContainer
                        component={Box}
                        sx={{
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            overflowX: "auto",
                            mb: 3,
                        }}
                    >
                        <Table size="small" sx={{ minWidth: 460 }}>
                            <TableHead>
                                <TableRow sx={{ background: BRAND.goldGlow }}>
                                    {["Field", "Example", "Meaning"].map((h) => (
                                        <TableCell key={h} sx={tcStyle({ color: BRAND.gold, fontFamily: "monospace", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", borderColor: BRAND.border })}>
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[
                                    { field: "1 — Device",      example: "UUID=abc123...",     meaning: "The device to mount. Use UUID (blkid) for stability." },
                                    { field: "2 — Mount point", example: "/mnt/data",          meaning: "Directory where the device will be accessible." },
                                    { field: "3 — FS type",     example: "ext4",               meaning: "Filesystem type: ext4, xfs, btrfs, vfat, swap, etc." },
                                    { field: "4 — Options",     example: "defaults,noatime",   meaning: "Mount options. 'defaults' = rw, suid, dev, exec, auto, nouser, async." },
                                    { field: "5 — Dump",        example: "0",                  meaning: "Backup with dump utility. Almost always 0 (disabled)." },
                                    { field: "6 — fsck pass",   example: "2",                  meaning: "fsck check order at boot. 0=skip, 1=root only, 2=all others." },
                                ].map(({ field, example, meaning }) => (
                                    <TableRow key={field} sx={{ "&:hover": { background: "rgba(255,255,255,0.02)" }, transition: "background 0.2s" }}>
                                        <TableCell sx={tcStyle({ color: BRAND.goldBright, fontFamily: "monospace", fontWeight: 700, borderColor: BRAND.border })}>{field}</TableCell>
                                        <TableCell sx={tcStyle({ fontFamily: "monospace", color: BRAND.codeText, borderColor: BRAND.border })}>{example}</TableCell>
                                        <TableCell sx={tcStyle({ borderColor: BRAND.border })}>{meaning}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Example fstab entries */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Example fstab Entries
                    </Typography>

                    {/* Styled code block showing fstab file content */}
                    <Box
                        sx={{
                            background: BRAND.codeBg,
                            border: `1px solid ${BRAND.border}`,
                            borderRadius: 2,
                            p: 2.5,
                            mb: 3,
                            overflowX: "auto",
                        }}
                    >
                        {[
                            { line: "# <device UUID>                              <mountpoint>  <type>   <options>          <dump> <pass>", isComment: true },
                            { line: "" },
                            { line: "# Root filesystem — always pass 1", isComment: true },
                            { line: "UUID=550e8400-e29b-41d4-a716-446655440000   /             ext4     defaults,noatime   0      1" },
                            { line: "" },
                            { line: "# Data partition", isComment: true },
                            { line: "UUID=a1b2c3d4-e5f6-7890-abcd-ef1234567890   /mnt/data     ext4     defaults,noatime   0      2" },
                            { line: "" },
                            { line: "# Swap partition", isComment: true },
                            { line: "UUID=12345678-1234-1234-1234-123456789abc   none          swap     sw                 0      0" },
                            { line: "" },
                            { line: "# USB / FAT32 drive (auto-mount, allows regular users to unmount)", isComment: true },
                            { line: "UUID=ABCD-1234                               /media/usb    vfat     defaults,uid=1000,gid=1000 0 0" },
                            { line: "" },
                            { line: "# NFS network share", isComment: true },
                            { line: "192.168.1.10:/exports/shared                /mnt/nfs      nfs      defaults,_netdev   0      0" },
                        ].map((entry, i) => (
                            <Typography
                                key={i}
                                sx={{
                                    fontFamily: "monospace",
                                    fontSize: { xs: 11, md: 12 },
                                    color: entry.isComment ? "rgba(255,255,255,0.3)" : BRAND.codeText,
                                    lineHeight: 1.9,
                                    whiteSpace: "pre",          // preserve spacing for column alignment
                                }}
                            >
                                {entry.line || "\u00A0"}
                            </Typography>
                        ))}
                    </Box>

                    {/* ── fstab management commands ── */}
                    <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14, mb: 1.2 }}>
                        🟡 Testing & Managing fstab
                    </Typography>
                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo blkid",                     comment: "Get UUIDs to paste into fstab" },
                            { cmd: "$ sudo mount -a",                  comment: "Mount everything listed in fstab (test without rebooting)" },
                            { cmd: "$ sudo findmnt --verify",          comment: "Validate fstab syntax — fix errors before rebooting!" },
                            { cmd: "$ systemctl daemon-reload",        comment: "Reload systemd unit files after editing fstab" },
                            { cmd: "$ cat /etc/fstab",                 comment: "Review current fstab entries" },
                        ]}
                    />

                    {/* Best practice box */}
                    <SuccessBox>
                        <strong>Best practice:</strong> Always run{" "}
                        <code>sudo findmnt --verify</code> and{" "}
                        <code>sudo mount -a</code> after editing fstab — before rebooting. A syntax error
                        in fstab can prevent the system from booting. If that happens, boot from a live USB
                        and fix the file manually.
                    </SuccessBox>

                    <ExpandableExample title="Swap file as an alternative to a swap partition" icon={<SwapVertIcon />}>
                        <Typography sx={{ color: BRAND.muted, fontSize: 13, mb: 2, lineHeight: 1.7 }}>
                            Modern best practice on cloud VMs and containers is to use a{" "}
                            <strong style={{ color: BRAND.white }}>swap file</strong> instead of a dedicated
                            partition. A swapfile can be created on any existing filesystem and resized without
                            repartitioning.
                        </Typography>
                        <CodeBlock
                            lines={[
                                { cmd: "$ sudo fallocate -l 4G /swapfile",        comment: "Create a 4 GiB swap file (use dd if fallocate isn't available)" },
                                { cmd: "$ sudo chmod 600 /swapfile",              comment: "Restrict permissions — swap files must not be world-readable" },
                                { cmd: "$ sudo mkswap /swapfile",                 comment: "Format it as swap" },
                                { cmd: "$ sudo swapon /swapfile",                 comment: "Activate it immediately" },
                                { cmd: "$ swapon --show",                         comment: "Confirm it is active" },
                                { cmd: "$ echo '/swapfile none swap sw 0 0' | sudo tee -a /etc/fstab", comment: "Make it persistent across reboots" },
                            ]}
                        />
                    </ExpandableExample>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 8 – LVM (LOGICAL VOLUME MANAGER)
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SpeedIcon />} label="LVM — Logical Volume Manager" />

                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 13, md: 15 }, lineHeight: 1.8, mb: 3 }}>
                        LVM adds a layer of abstraction above physical disks. Instead of partitions with
                        fixed sizes, you create{" "}
                        <strong style={{ color: BRAND.white }}>logical volumes</strong> that can be
                        resized, snapshotted, and span multiple disks. Most production Linux servers use
                        LVM for flexibility. The three layers are:{" "}
                        Physical Volumes (PV) → Volume Groups (VG) → Logical Volumes (LV).
                    </Typography>

                    <CodeBlock
                        lines={[
                            { cmd: "$ sudo pvcreate /dev/sdb",              comment: "Initialise a physical disk as a PV (Physical Volume)" },
                            { cmd: "$ sudo pvs",                            comment: "List all Physical Volumes" },
                            { cmd: "$ sudo vgcreate vg_data /dev/sdb",      comment: "Create a Volume Group named 'vg_data' from the PV" },
                            { cmd: "$ sudo vgs",                            comment: "List all Volume Groups" },
                            { cmd: "$ sudo lvcreate -L 20G -n lv_home vg_data", comment: "Create a 20 GiB Logical Volume named 'lv_home'" },
                            { cmd: "$ sudo lvs",                            comment: "List all Logical Volumes" },
                            { cmd: "$ sudo mkfs.ext4 /dev/vg_data/lv_home",comment: "Format the LV as ext4" },
                            { cmd: "$ sudo mount /dev/vg_data/lv_home /home",comment: "Mount it at /home" },
                            { cmd: "$ sudo lvextend -L +10G /dev/vg_data/lv_home", comment: "Extend the LV by 10 GiB (online, no unmounting needed)" },
                            { cmd: "$ sudo resize2fs /dev/vg_data/lv_home", comment: "Grow the ext4 filesystem to fill the new LV size" },
                        ]}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 9 – WHAT YOU HAVE LEARNED
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="What You've Learned" />

                    <Grid container spacing={1.5}>
                        {[
                            "How to identify disks and partitions with lsblk, fdisk, and blkid",
                            "The difference between MBR and GPT partition tables",
                            "How to create partitions interactively with fdisk and parted",
                            "How to script partition creation with parted -s and sgdisk",
                            "How to choose the right filesystem for your use case",
                            "How to format partitions with mkfs.ext4, mkfs.xfs, and mkswap",
                            "How to mount and unmount filesystems with custom options",
                            "How to use bind mounts and loop device mounts",
                            "How to write correct /etc/fstab entries using UUIDs",
                            "How to validate fstab changes before rebooting",
                            "How to create and activate a swap file",
                            "The basics of LVM: PVs, VGs, LVs, and online resize",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1, flexShrink: 0 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* ══════════════════════════════════════════════
            SECTION 10 – RESOURCES
        ══════════════════════════════════════════════ */}
                <Box sx={{ mb: 4 }}>
                    <SectionTitle icon={<LinkIcon />} label="Further Resources" />

                    <ResourceLink
                        href="https://linuxize.com/post/how-to-mount-and-unmount-drives-in-linux/"
                        label="Mount & Unmount Drives — Linuxize"
                        sub="Step-by-step guide covering temporary and permanent mounts on any Linux distro."
                    />
                    <ResourceLink
                        href="https://wiki.archlinux.org/title/Fdisk"
                        label="Arch Wiki — fdisk"
                        sub="The most detailed fdisk reference available, including GPT workflow examples."
                    />
                    <ResourceLink
                        href="https://man7.org/linux/man-pages/man5/fstab.5.html"
                        label="fstab(5) — Linux Man Page"
                        sub="Official man page covering every field, filesystem type, and mount option."
                    />
                    <ResourceLink
                        href="https://wiki.archlinux.org/title/LVM"
                        label="Arch Wiki — LVM"
                        sub="Comprehensive LVM guide: setup, snapshots, thin provisioning, and migration."
                    />
                    <ResourceLink
                        href="https://www.kernel.org/doc/html/latest/admin-guide/device-mapper/index.html"
                        label="Linux Kernel — Device Mapper"
                        sub="Official documentation for the device mapper subsystem that powers LVM."
                    />
                    <ResourceLink
                        href="https://btrfs.wiki.kernel.org/index.php/Getting_started"
                        label="Btrfs Wiki — Getting Started"
                        sub="Official Btrfs documentation including snapshots, RAID modes, and compression."
                    />
                </Box>

                {/* ── Footer pro tip ── */}
                <Box
                    sx={{
                        mt: 2,
                        p: 2.5,
                        border: `1px solid ${BRAND.border}`,
                        borderRadius: 2,
                        background: BRAND.goldGlow,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                    }}
                >
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0, mt: 0.1 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13, lineHeight: 1.7 }}>
                        <strong style={{ color: BRAND.white }}>Pro tip for juniors:</strong> Never practise
                        partitioning and formatting on a real machine until you are confident. Use{" "}
                        <code style={{ color: BRAND.goldBright }}>VirtualBox</code> or{" "}
                        <code style={{ color: BRAND.goldBright }}>UTM</code> to create a VM with a spare
                        virtual disk attached. You can safely destroy and rebuild it as many times as needed
                        without any risk to your real data.
                    </Typography>
                </Box>

            </Container>
        </Box>
    );
}
